import { Stack } from "@mantine/core"
import { useRef } from "react"
import { useTranslation } from "react-i18next"

import type { ContentFilteringProps } from "@tfg/types/types/communication-studio/configurations.types"

import { DataFetchWrapper } from "~/components/DataFetchWrapper"
import {
  useGetContentFilteringConfigurations,
  useUpdateContentFiltering,
} from "~/gen/api"
import {
  ContentFilteringTypesEnum,
  useGetContentFilteringRegistry,
} from "~/pages/Brand/CommunicationStudio/Content/config/component-renderer"
import { ComponentTypeRenderer } from "~/pages/Brand/CommunicationStudio/Languages/components/ComponentTypeRenderer"
import { useComponentCreation } from "~/pages/Brand/CommunicationStudio/contexts/componentCreation/useComponentCreation"
import { useAuth } from "~/store/auth-context"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

export const ContentFiltering = () => {
  const { productSheetGroupId } = useComponentCreation()
  const { t } = useTranslation("communicationStudio")
  const { user } = useAuth()
  const clientId = user?.organization?.id

  const query = useGetContentFilteringConfigurations(productSheetGroupId)
  const contentFilteringRegistry = useGetContentFilteringRegistry()

  // Track which configuration type is being updated
  const currentUpdateTypeRef = useRef<string | null>(null)

  const { mutate: updateContentFiltering } = useUpdateContentFiltering({
    mutation: {
      onSuccess: () => {
        const translationKey =
          currentUpdateTypeRef.current ===
          ContentFilteringTypesEnum.ECOSCORE_DISPLAY_MODE
            ? "ecoScoreDisplayMode.update.success"
            : "standardsConfiguration.update.success"

        showSuccessNotification({
          title: t(`tabs.content.contentFiltering.${translationKey}.title`),
          message: t(`tabs.content.contentFiltering.${translationKey}.message`),
        })
        query.refetch()
        currentUpdateTypeRef.current = null
      },
      onError: (error) => {
        const translationKey =
          currentUpdateTypeRef.current ===
          ContentFilteringTypesEnum.ECOSCORE_DISPLAY_MODE
            ? "ecoScoreDisplayMode.update.error"
            : "standardsConfiguration.update.error"

        showAxiosError({
          error,
          notificationTitle: t(
            `tabs.content.contentFiltering.${translationKey}.title`
          ),
          notificationMessage: t(
            `tabs.content.contentFiltering.${translationKey}.message`
          ),
          showNotification: true,
        })
        currentUpdateTypeRef.current = null
      },
    },
  })

  if (!clientId) return <div>No client id</div>

  return (
    <DataFetchWrapper query={query}>
      {({ data }) => {
        const configurations = data?.data

        const availableConfigurations = Object.values(contentFilteringRegistry)
        const configurationsToShow = availableConfigurations
        return (
          <Stack>
            {configurationsToShow.map((configMetadata) => (
              <ComponentTypeRenderer
                key={configMetadata.type}
                componentType={configMetadata.type}
                textContent={null}
                setTextContent={() => {}}
                updateTextContent={() => {}}
                configurations={configurations}
                setConfigurations={() => {}}
                updateModalConfigurations={(
                  updatedConfigs: Partial<ContentFilteringProps> | null
                ) => {
                  // Store the current type being updated
                  currentUpdateTypeRef.current = configMetadata.type
                  updateContentFiltering({
                    productSheetGroupId,
                    data: {
                      ...updatedConfigs,
                    },
                  })
                }}
                additionalProps={{
                  clientId,
                }}
              />
            ))}
          </Stack>
        )
      }}
    </DataFetchWrapper>
  )
}

// t("tabs.content.contentFiltering.ecoScoreDisplayMode.subtitle")
// t("tabs.content.contentFiltering.ecoScoreDisplayMode.update.success.title")
// t("tabs.content.contentFiltering.ecoScoreDisplayMode.update.success.message")
// t("tabs.content.contentFiltering.ecoScoreDisplayMode.update.error.title")
// t("tabs.content.contentFiltering.ecoScoreDisplayMode.update.error.message")
// t("tabs.content.contentFiltering.standardsToShow.subtitle")
// t("tabs.content.contentFiltering.standardsConfiguration.update.success.title")
// t("tabs.content.contentFiltering.standardsConfiguration.update.success.message")
// t("tabs.content.contentFiltering.standardsConfiguration.update.error.title")
// t("tabs.content.contentFiltering.standardsConfiguration.update.error.message")
